import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus, RefreshCw, Loader2, Trophy, Target, BarChart3, GitCompare } from 'lucide-react';
import { adminGet } from '../data/api';

const ADMIN_PIN = 'pointedge2024';
const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };
const innerCard = { background: '#0D0D0F', border: '1px solid #1F1F22' };

const TrendArrow = ({ trend }) => {
  if (!trend || trend === 'flat') return null;
  if (trend === 'up') return <TrendingUp size={11} className="text-emerald-400 ml-1 inline" />;
  return <TrendingDown size={11} className="text-red-400 ml-1 inline" />;
};

const STAT_TIPS = {
  'Segnali': 'Numero totale di scommesse value identificate dal modello',
  'Hit Rate': 'Percentuale di scommesse vinte sul totale',
  'ROI': 'Return on Investment: profitto medio per unità scommessa',
  'Profitto': 'Profitto/perdita netto in unità',
  'Vinte': 'Numero di scommesse vinte',
  'Perse': 'Numero di scommesse perse',
};

const BUCKET_TIPS = {
  'Bucket': 'Range di probabilità stimata dal modello per il side raccomandato',
  'N': 'Numero di segnali in questo range di probabilità',
  'HR%': 'Hit Rate: % di scommesse vinte in questo bucket',
  'ROI%': 'Return on Investment medio per scommessa in questo bucket',
  'PnL': 'Profit & Loss totale in unità per questo bucket',
};

function Tip({ text, children }) {
  return (
    <span className="relative group cursor-help">
      {children}
      <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2.5 py-1.5 rounded text-[10px] text-zinc-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg"
        style={{ background: '#27272A', border: '1px solid #3F3F46' }}>
        {text}
      </span>
    </span>
  );
}

function Stat({ label, value, color = 'text-zinc-200', trend }) {
  const tip = STAT_TIPS[label];
  return (
    <div className="rounded-sm p-2.5" style={innerCard} data-testid={`stat-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="text-zinc-500 text-[10px] uppercase tracking-wider">
        {tip ? <Tip text={tip}>{label}</Tip> : label}
      </div>
      <div className={`text-base font-bold mt-0.5 ${color}`} style={mono}>
        {value}
        <TrendArrow trend={trend} />
      </div>
    </div>
  );
}

function WindowTabs({ active, onChange }) {
  const tabs = ['1d', '7d', '14d', '30d', 'all_time'];
  const labels = { '1d': '1G', '7d': '7G', '14d': '14G', '30d': '30G', 'all_time': 'Tutto' };
  return (
    <div className="flex gap-1" data-testid="window-tabs">
      {tabs.map(t => (
        <button key={t} onClick={() => onChange(t)}
          className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
            active === t ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'
          }`}>{labels[t]}</button>
      ))}
    </div>
  );
}

function MarketCard({ title, icon: Icon, iconColor, data, window, testId }) {
  if (!data) return null;
  const w = data.windows?.[window] || {};
  const daily = data.windows?.daily_pnl || [];

  const roiColor = w.roi_pct > 0 ? 'text-emerald-400' : w.roi_pct < 0 ? 'text-red-400' : 'text-zinc-300';
  const profitColor = w.profit > 0 ? 'text-emerald-400' : w.profit < 0 ? 'text-red-400' : 'text-zinc-300';
  const hrColor = w.hit_rate >= 55 ? 'text-emerald-400' : w.hit_rate >= 50 ? 'text-amber-400' : w.hit_rate != null ? 'text-red-400' : 'text-zinc-500';

  // Sparkline from daily PnL
  const maxAbs = Math.max(1, ...daily.map(d => Math.abs(d.pnl)));
  const sparkH = 28;

  return (
    <div className="rounded-md p-4" style={card} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className={iconColor} />
          <span className="text-sm font-semibold text-zinc-200" style={heading}>{title}</span>
          <span className="text-[10px] text-zinc-600 ml-1">{data.model}</span>
        </div>
        {daily.length > 1 && (
          <svg width={daily.length * 8} height={sparkH} className="opacity-60">
            {daily.map((d, i) => {
              const h = (Math.abs(d.pnl) / maxAbs) * (sparkH - 4);
              const y = d.pnl >= 0 ? sparkH / 2 - h : sparkH / 2;
              return <rect key={i} x={i * 8} y={y} width={6} height={Math.max(1, h)}
                rx={1} fill={d.pnl >= 0 ? '#34d399' : '#f87171'} />;
            })}
            <line x1={0} y1={sparkH / 2} x2={daily.length * 8} y2={sparkH / 2}
              stroke="#3f3f46" strokeWidth={0.5} />
          </svg>
        )}
      </div>

      {w.bets == null || w.bets === 0 ? (
        <div className="text-zinc-600 text-xs py-4 text-center">Nessun dato per questo periodo</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Stat label="Segnali" value={w.bets} />
          <Stat label="Hit Rate" value={`${w.hit_rate}%`} color={hrColor} trend={w.trend?.hit_rate} />
          <Stat label="ROI" value={`${w.roi_pct > 0 ? '+' : ''}${w.roi_pct}%`} color={roiColor} trend={w.trend?.roi_pct} />
          <Stat label="Profitto" value={`${w.profit > 0 ? '+' : ''}${w.profit}u`} color={profitColor} trend={w.trend?.profit} />
          <Stat label="Vinte" value={w.wins} color="text-emerald-400" />
          <Stat label="Perse" value={w.losses} color="text-red-400" />
        </div>
      )}

      {/* HC breakdown */}
      {data.breakdown && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {Object.entries(data.breakdown).map(([k, v]) => (
            <div key={k} className="rounded-sm p-2" style={innerCard}>
              <div className="text-[10px] text-zinc-500 uppercase">{k === 'cover' ? 'Cover (-1.5)' : 'Not Cover (+1.5)'}</div>
              {v.bets > 0 ? (
                <div className="text-xs mt-1" style={mono}>
                  <span className="text-zinc-300">{v.bets} bet</span>
                  <span className="mx-1.5 text-zinc-600">|</span>
                  <span className={v.hit_rate >= 55 ? 'text-emerald-400' : 'text-zinc-300'}>{v.hit_rate}% HR</span>
                  <span className="mx-1.5 text-zinc-600">|</span>
                  <span className={v.roi_pct > 0 ? 'text-emerald-400' : 'text-red-400'}>{v.roi_pct > 0 ? '+' : ''}{v.roi_pct}% ROI</span>
                  <span className="mx-1.5 text-zinc-600">|</span>
                  <span className={v.profit > 0 ? 'text-emerald-400' : 'text-red-400'}>{v.profit > 0 ? '+' : ''}{v.profit}u</span>
                </div>
              ) : <div className="text-[10px] text-zinc-600 mt-1">—</div>}
            </div>
          ))}
        </div>
      )}

      {/* Probability buckets - from active window */}
      {(w.buckets?.length > 0) && (
        <div className="mt-3 overflow-x-auto">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 px-2">Probability Buckets</div>
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-[10px] uppercase">
              <th className="text-left py-1 px-2"><Tip text={BUCKET_TIPS['Bucket']}>Bucket</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['N']}>N</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['HR%']}>HR%</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['ROI%']}>ROI%</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['PnL']}>PnL</Tip></th>
            </tr></thead>
            <tbody>
              {w.buckets.map(b => {
                const bRoi = b.roi_pct ?? 0;
                const t = b.trend || {};
                return (
                  <tr key={b.bucket} className="border-t border-zinc-800/40">
                    <td className="py-1 px-2 text-zinc-400">{b.bucket}</td>
                    <td className="py-1 px-2 text-zinc-300 text-right" style={mono}>
                      {b.count}<TrendArrow trend={t.count} />
                    </td>
                    <td className="py-1 px-2 text-right" style={mono}>
                      <span className={(b.hit_rate || 0) >= 0.55 ? 'text-emerald-400' : 'text-zinc-300'}>
                        {b.hit_rate != null ? (b.hit_rate * 100).toFixed(1) : '—'}%
                      </span>
                      <TrendArrow trend={t.hit_rate} />
                    </td>
                    <td className={`py-1 px-2 text-right font-medium ${bRoi > 0 ? 'text-emerald-400' : 'text-red-400'}`} style={mono}>
                      {bRoi > 0 ? '+' : ''}{bRoi.toFixed(1)}%
                      <TrendArrow trend={t.roi_pct} />
                    </td>
                    <td className="py-1 px-2 text-zinc-400 text-right" style={mono}>
                      {b.profit_units > 0 ? '+' : ''}{b.profit_units?.toFixed(1)}u
                      <TrendArrow trend={t.profit} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Confidence buckets */}
      {(w.conf_buckets?.length > 0) && (
        <div className="mt-3 overflow-x-auto">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 px-2">Confidence Buckets</div>
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-[10px] uppercase">
              <th className="text-left py-1 px-2"><Tip text="Range di confidence del modello MW per il match">Conf.</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['N']}>N</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['HR%']}>HR%</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['ROI%']}>ROI%</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['PnL']}>PnL</Tip></th>
            </tr></thead>
            <tbody>
              {w.conf_buckets.map(b => {
                const bRoi = b.roi_pct ?? 0;
                const t = b.trend || {};
                return (
                  <tr key={b.bucket} className="border-t border-zinc-800/40">
                    <td className="py-1 px-2 text-zinc-400">{b.bucket}</td>
                    <td className="py-1 px-2 text-zinc-300 text-right" style={mono}>
                      {b.count}<TrendArrow trend={t.count} />
                    </td>
                    <td className="py-1 px-2 text-right" style={mono}>
                      <span className={(b.hit_rate || 0) >= 0.55 ? 'text-emerald-400' : 'text-zinc-300'}>
                        {b.hit_rate != null ? (b.hit_rate * 100).toFixed(1) : '—'}%
                      </span>
                      <TrendArrow trend={t.hit_rate} />
                    </td>
                    <td className={`py-1 px-2 text-right font-medium ${bRoi > 0 ? 'text-emerald-400' : 'text-red-400'}`} style={mono}>
                      {bRoi > 0 ? '+' : ''}{bRoi.toFixed(1)}%
                      <TrendArrow trend={t.roi_pct} />
                    </td>
                    <td className="py-1 px-2 text-zinc-400 text-right" style={mono}>
                      {b.profit_units > 0 ? '+' : ''}{b.profit_units?.toFixed(1)}u
                      <TrendArrow trend={t.profit} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OUCard({ data }) {
  if (!data) return (
    <div className="rounded-md p-4" style={card} data-testid="ou-card">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={16} className="text-blue-400" />
        <span className="text-sm font-semibold text-zinc-200" style={heading}>Over/Under</span>
      </div>
      <div className="text-zinc-600 text-xs py-4 text-center">Dati non disponibili</div>
    </div>
  );

  const g = data.global || {};
  const roiColor = g.roi_pct > 0 ? 'text-emerald-400' : g.roi_pct < 0 ? 'text-red-400' : 'text-zinc-300';
  const hrPct = g.hit_rate != null ? (g.hit_rate * 100).toFixed(1) : '—';
  const hrColor = (g.hit_rate || 0) >= 0.55 ? 'text-emerald-400' : (g.hit_rate || 0) >= 0.50 ? 'text-amber-400' : 'text-zinc-300';

  return (
    <div className="rounded-md p-4" style={card} data-testid="ou-card">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={16} className="text-blue-400" />
        <span className="text-sm font-semibold text-zinc-200" style={heading}>Over/Under</span>
        <span className="text-[10px] text-zinc-600 ml-1">{data.model_version}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
        <Stat label="Settled" value={data.settled_evals} />
        <Stat label="Segnali" value={g.total_signals} />
        <Stat label="Hit Rate" value={`${hrPct}%`} color={hrColor} />
        <Stat label="ROI" value={`${g.roi_pct > 0 ? '+' : ''}${g.roi_pct?.toFixed(1)}%`} color={roiColor} />
        <Stat label="Profitto" value={`${g.profit_units > 0 ? '+' : ''}${g.profit_units?.toFixed(1)}u`}
          color={g.profit_units > 0 ? 'text-emerald-400' : 'text-red-400'} />
        <Stat label="Brier" value={g.brier?.toFixed(4)} />
      </div>
      {/* Bucket table */}
      {data.buckets?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-[10px] uppercase">
              <th className="text-left py-1 px-2"><Tip text={BUCKET_TIPS['Bucket']}>Bucket</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['N']}>N</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['HR%']}>HR%</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['ROI%']}>ROI%</Tip></th>
              <th className="text-right py-1 px-2"><Tip text={BUCKET_TIPS['PnL']}>PnL</Tip></th>
            </tr></thead>
            <tbody>
              {data.buckets.map(b => {
                const bRoi = b.roi_pct ?? 0;
                return (
                  <tr key={b.bucket} className="border-t border-zinc-800/40">
                    <td className="py-1 px-2 text-zinc-400">{b.bucket}</td>
                    <td className="py-1 px-2 text-zinc-300 text-right" style={mono}>{b.count}</td>
                    <td className="py-1 px-2 text-right" style={mono}>
                      <span className={(b.hit_rate || 0) >= 0.55 ? 'text-emerald-400' : 'text-zinc-300'}>
                        {b.hit_rate != null ? (b.hit_rate * 100).toFixed(1) : '—'}%
                      </span>
                    </td>
                    <td className={`py-1 px-2 text-right font-medium ${bRoi > 0 ? 'text-emerald-400' : 'text-red-400'}`} style={mono}>
                      {bRoi > 0 ? '+' : ''}{bRoi.toFixed(1)}%
                    </td>
                    <td className="py-1 px-2 text-zinc-400 text-right" style={mono}>
                      {b.profit_units > 0 ? '+' : ''}{b.profit_units?.toFixed(1)}u
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function PerformanceMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState('all_time');

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminGet('/tt/admin/performance-monitor', ADMIN_PIN);
      setData(d);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-zinc-500" data-testid="perf-monitor-loading">
      <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
    </div>
  );

  if (!data) return (
    <div className="text-zinc-500 text-center py-16" data-testid="perf-monitor-error">
      Errore nel caricamento dei dati
    </div>
  );

  // Totals
  const mwAll = data.mw?.windows?.all_time || {};
  const lrAll = data.mw_lr?.windows?.all_time || {};
  const hcAll = data.hc?.windows?.all_time || {};
  const ouProfit = data.ou?.global?.profit_units || 0;
  const totalProfit = (mwAll.profit || 0) + (hcAll.profit || 0) + ouProfit;
  const totalBets = (mwAll.bets || 0) + (hcAll.bets || 0) + (data.ou?.global?.total_signals || 0);
  const totalColor = totalProfit > 0 ? 'text-emerald-400' : totalProfit < 0 ? 'text-red-400' : 'text-zinc-300';

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="perf-monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-zinc-100" style={heading}>Performance Monitor</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            {data.total_settled_matches} partite settled | {totalBets} segnali totali
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] text-zinc-500 uppercase">Profitto Totale</div>
            <div className={`text-lg font-bold ${totalColor}`} style={mono}>
              {totalProfit > 0 ? '+' : ''}{totalProfit.toFixed(1)}u
            </div>
          </div>
          <button onClick={load} className="p-2 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors"
            data-testid="perf-refresh-btn">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Window tabs for MW and HC */}
      <div className="mb-4">
        <WindowTabs active={window} onChange={setWindow} />
      </div>

      {/* Market cards */}
      <div className="space-y-4">
        <MarketCard title="MW Pascal-Sierpinski" icon={Trophy} iconColor="text-emerald-400"
          data={data.mw} window={window} testId="mw-card" />

        <MarketCard title="MW Logistic Regression" icon={GitCompare} iconColor="text-indigo-400"
          data={data.mw_lr} window={window} testId="mw-lr-card" />

        <MarketCard title="Handicap" icon={Target} iconColor="text-purple-400"
          data={data.hc} window={window} testId="hc-card" />

        <OUCard data={data.ou} />
      </div>
    </div>
  );
}
